import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PencilSimple, WarningCircle, Key } from '@phosphor-icons/react';

export default function EditBotPage() {
  const { user } = useAuth();
  const { selectedBot, bots, setBots } = useOutletContext();
  const [action, setAction] = useState('setname');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [updatingToken, setUpdatingToken] = useState(false);

  const bot = bots.find(b => b.id === selectedBot);

  if (!selectedBot || !bot) {
    return (
      <div className="space-y-4" data-testid="edit-bot-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Edit Bot</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          Select a bot from the sidebar to edit
        </div>
      </div>
    );
  }

  const actions = [
    { key: 'setname', label: 'Change Bot Name', placeholder: 'New bot name' },
    { key: 'setdescription', label: 'Change Description', placeholder: 'New description' },
    { key: 'setabouttext', label: 'Change About Text', placeholder: 'New about text' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.post(`/bots/${selectedBot}/edit-telegram`, { action, value });
      toast.success('Bot updated via Telegram');
      setValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="edit-bot-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Edit Bot</h1>
        <p className="text-sm text-muted-foreground mt-1">Edit {bot.bot_name} (@{bot.bot_username}) via Telegram API</p>
      </div>

      {(user?.role === 'super_admin' || user?.permissions?.includes('bot_automation')) && (
        <div className="border border-indigo-500/30 rounded-lg p-3.5 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span>⚡</span>
            <span className="font-semibold text-foreground">Bot Automation & Visual Flows</span>
            <span className="text-muted-foreground hidden sm:inline">- Connect this bot to automated AI flows</span>
          </div>
          <Link to="/app/bot-automation" className="text-primary font-bold hover:underline">
            Manage Bot Automation ↗
          </Link>
        </div>
      )}

      <div className="border border-border rounded-md p-5 space-y-4">
        <h4 className="font-medium text-base">Bot Properties</h4>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Action</Label>
            <select
              data-testid="edit-action-select"
              value={action}
              onChange={e => setAction(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
            >
              {actions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>New Value</Label>
            <Input
              data-testid="edit-value-input"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={actions.find(a => a.key === action)?.placeholder}
              required
            />
          </div>
          <Button data-testid="edit-submit-btn" type="submit" disabled={submitting} className="gap-2">
            <PencilSimple size={18} /> {submitting ? 'Updating...' : 'Update'}
          </Button>
        </form>
      </div>

      <div className="border border-border rounded-md p-5 space-y-4">
        <h4 className="font-medium text-base flex items-center gap-2"><Key size={18} /> Update Bot Token</h4>
        <p className="text-xs text-muted-foreground">Replace the API token if you regenerated it from BotFather. Webhook will be updated automatically.</p>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (!newToken.trim()) return;
          setUpdatingToken(true);
          try {
            const { data } = await API.put(`/bots/${selectedBot}`, { token: newToken.trim() });
            toast.success(`Token updated! Bot: ${data.bot.bot_name} (@${data.bot.bot_username})`);
            setNewToken('');
            if (setBots) setBots(prev => prev.map(b => b.id === selectedBot ? data.bot : b));
          } catch (err) { toast.error(err.response?.data?.detail || 'Invalid token'); }
          finally { setUpdatingToken(false); }
        }} className="flex gap-2">
          <Input value={newToken} onChange={e => setNewToken(e.target.value)} placeholder="Paste new bot token from BotFather" className="flex-1" data-testid="update-token-input" required />
          <Button type="submit" disabled={updatingToken} data-testid="update-token-btn" className="gap-1.5 shrink-0">
            <Key size={14} /> {updatingToken ? 'Verifying...' : 'Update Token'}
          </Button>
        </form>
      </div>

      <div className="border border-border rounded-md p-5 space-y-3">
        <h4 className="font-medium text-base">Profile Photo</h4>
        <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50 border border-border">
          <WarningCircle size={20} className="text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Profile photo must be changed via BotFather</p>
            <p>Open Telegram, go to @BotFather, and use the <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/setuserpic</code> command to change your bot&apos;s profile photo. This cannot be done through the Bot API.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
